import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface PendingInvitation {
  id: string
  invitedEmail: string
  status: 'pending' | 'accepted' | 'revoked'
  expiresAt: string
  createdAt: string
  acceptedAt: string | null
  schoolName: string | null
  relationshipType: 'employee' | 'contractor' | null
  hourlyRate: number | null
  jobTitle: string | null
  roleCategory: 'instructor' | 'administrator' | 'receptionist' | 'finance' | 'other' | null
  employmentType: 'full_time' | 'part_time' | 'casual' | null
}

interface CreateResult {
  ok: boolean
  code?: string
  emailed?: boolean
  token?: string
}

interface UseInstructorInvitationsReturn {
  invitations: PendingInvitation[]
  loading: boolean
  error: string | null
  creating: boolean
  revokingId: string | null
  resending: boolean
  createInvitation: (schoolId: string, email: string) => Promise<CreateResult>
  revokeInvitation: (id: string) => Promise<{ ok: boolean; code?: string }>
  resendInvitations: (ids: string[]) => Promise<{ ok: boolean; succeeded: number; failed: number }>
  refresh: () => void
}

/**
 * Lists pending instructor invitations for the authenticated school admin's own school
 * (RLS "instructor_invitations: school admin read", migration 054, scopes this to the
 * caller's administered school automatically) and exposes create/revoke actions that call
 * the invite-instructor Edge Function / revoke_instructor_invitation RPC respectively.
 *
 * Client-side filtering here (status, expiry) is presentation only — every mutating action
 * re-validates authorization and state server-side; this hook never assumes otherwise.
 */
export function useInstructorInvitations(): UseInstructorInvitationsReturn {
  const { user } = useAuth()
  const [invitations, setInvitations] = useState<PendingInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [resending, setResending] = useState(false)

  const fetchInvitations = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      // RLS scopes this to every school the caller administers (potentially more than
      // one — is_school_admin_for() is evaluated per-row against that row's own
      // school_id); no client-side school filter is needed or possible to get wrong.
      // schools(name) is included so the UI can label which school each invitation
      // belongs to when the admin manages more than one.
      const { data, error: fetchErr } = await supabase
        .from('instructor_invitations')
        .select('id, invited_email, status, expires_at, created_at, accepted_at, relationship_type, hourly_rate, job_title, role_category, employment_type, schools(name)')
        .order('created_at', { ascending: false })

      if (fetchErr) { setError('Could not load invitations.'); return }

      setInvitations(
        (data ?? []).map((row: any) => ({
          id: row.id,
          invitedEmail: row.invited_email,
          status: row.status,
          expiresAt: row.expires_at,
          createdAt: row.created_at,
          acceptedAt: row.accepted_at,
          schoolName: row.schools?.name ?? null,
          relationshipType: row.relationship_type,
          hourlyRate: row.hourly_rate === null || row.hourly_rate === undefined ? null : Number(row.hourly_rate),
          jobTitle: row.job_title ?? null,
          roleCategory: row.role_category ?? null,
          employmentType: row.employment_type ?? null,
        }))
      )
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchInvitations() }, [fetchInvitations])

  const createInvitation = useCallback(async (
    schoolId: string, email: string,
  ): Promise<CreateResult> => {
    if (creating) return { ok: false, code: 'busy' }
    setCreating(true)
    try {
      // schoolId is the admin's own, explicit selection (never implicitly inferred) —
      // the server independently re-verifies admin authority for this exact school
      // inside create_instructor_invitation() regardless of what is sent here.
      // Schools may only invite EMPLOYEES (migration 078) — a contractor's only path onto
      // a roster is their own request_contractor_affiliation() + the school's approval
      // (see useSchoolAffiliationRequests.ts) — so this always sends 'employee'/null;
      // create_instructor_invitation() independently rejects 'contractor' regardless of
      // what any caller sends here.
      const { data, error: fnError } = await supabase.functions.invoke('invite-instructor', {
        body: { schoolId, email, relationshipType: 'employee', hourlyRate: null },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      if (data?.ok) await fetchInvitations()
      return data as CreateResult
    } finally {
      setCreating(false)
    }
  }, [creating, fetchInvitations])

  const revokeInvitation = useCallback(async (id: string) => {
    setRevokingId(id)
    try {
      const { data, error: rpcError } = await supabase.rpc('revoke_instructor_invitation', {
        p_invitation_id: id,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string } | null
      if (result?.ok) await fetchInvitations()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setRevokingId(null)
    }
  }, [fetchInvitations])

  // Bulk action for the Employees roster's "Resend invite" button: calls the Edge
  // Function once per selected invitation (resend_instructor_invitation re-tokens the SAME
  // row rather than creating a new one, so this never duplicates invitations). Each call
  // is independent — one failure doesn't block the others — and the full list is refreshed
  // once at the end rather than after every individual call.
  const resendInvitations = useCallback(async (ids: string[]) => {
    if (resending || ids.length === 0) return { ok: false, succeeded: 0, failed: 0 }
    setResending(true)
    try {
      const results = await Promise.all(ids.map(async id => {
        const { data, error: fnError } = await supabase.functions.invoke('resend-instructor-invitation', {
          body: { invitationId: id },
        })
        return !fnError && data?.ok
      }))
      const succeeded = results.filter(Boolean).length
      await fetchInvitations()
      return { ok: succeeded === ids.length, succeeded, failed: ids.length - succeeded }
    } finally {
      setResending(false)
    }
  }, [resending, fetchInvitations])

  return {
    invitations,
    loading,
    error,
    creating,
    resending,
    resendInvitations,
    revokingId,
    createInvitation,
    revokeInvitation,
    refresh: fetchInvitations,
  }
}
